import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { APIError } from '../../types/api/api';
import {
  AuthDTOWithStamp,
  ChangeAvatarDTO,
  ChangePasswordDTO,
  LoginDTO,
} from '../../types/auth/auth';
import { Permission } from '../../types/auth/permissions';
import { User } from '../../types/users/users';

export type AuthState = {
  loading: boolean;
  errors: APIError | null;
  updated_at: Date | null;
  user: null | {
    permissions: Permission[];
    data: User;
  };
};

export type AvatarUpdate = {
  imagen_perfil: string;
  updated_at: Date;
};

export const initialState: AuthState = {
  loading: false,
  errors: null,
  updated_at: null,
  user: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login(state, _action: PayloadAction<LoginDTO>) {
      state.loading = true;
      state.errors = null;
      state.user = null;
      state.updated_at = null;
    },
    loginOk(state, action: PayloadAction<AuthDTOWithStamp>) {
      const { user, permissions } = action.payload;

      state.loading = false;
      state.errors = null;
      state.user = {
        permissions,
        data: user,
      };
      state.updated_at = action.payload.updated_at;
    },
    loginKo(state, action: PayloadAction<APIError>) {
      state.loading = false;
      state.errors = action.payload;
      state.user = null;
      state.updated_at = null;
    },
    logout() {
      return { ...initialState };
    },
    editProfile(state, _action: PayloadAction<User>) {
      state.loading = true;
      state.errors = null;
    },
    editProfileOk(state, action: PayloadAction<User>) {
      if (state.user) state.user.data = action.payload;
      state.loading = false;
      state.errors = null;
      state.updated_at = new Date();
    },
    editProfileKo(state, action: PayloadAction<APIError>) {
      if (state.user) state.user.data = {} as User;
      state.loading = false;
      state.errors = action.payload;
      state.updated_at = null;
    },
    changePassword(state, _action: PayloadAction<ChangePasswordDTO>) {
      state.loading = true;
      state.errors = null;
    },
    changePasswordOk(state) {
      state.loading = false;
      state.errors = null;
    },
    changePasswordKo(state, action: PayloadAction<APIError>) {
      state.loading = false;
      state.errors = action.payload;
    },
    changeAvatar(state, _action: PayloadAction<ChangeAvatarDTO>) {
      state.loading = true;
      state.errors = null;
    },
    changeAvatarOk(state, action: PayloadAction<User>) {
      state.loading = false;
      state.errors = null;
      if (!state.user) {
        state.updated_at = null;
        throw new Error('Updated avatar of a non logged in user?');
      }
      state.user.data = action.payload;
      state.updated_at = new Date();
    },
    changeAvatarKo(state, action: PayloadAction<APIError>) {
      state.loading = false;
      state.errors = action.payload;
    },
    updateProfileImage(state, action: PayloadAction<AvatarUpdate>) {
      state.updated_at = action.payload.updated_at;
      if (state.user) {
        state.user.data.imagen_perfil = action.payload.imagen_perfil;
      }
    },
  },
});

export const {
  login,
  loginOk,
  loginKo,
  logout,
  editProfile,
  changePassword,
  changePasswordOk,
  changePasswordKo,
  changeAvatar,
  changeAvatarOk,
  changeAvatarKo,
} = authSlice.actions;

export default authSlice;
